import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { supabase } from '../utils/supabaseStorage';
import { useAppStore } from '../store/useAppStore';
import { isCreatorRole } from '../types/roles';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

type InstanceRow = {
  id: string;
  agent_template_id: string;
  status: string;
  updated_at: string;
  /** Supabase types may infer embedded FK as object or single-element array */
  agent_templates: { name: string } | { name: string }[] | null;
};

function templateName(tpl: InstanceRow['agent_templates']): string {
  if (!tpl) return 'Agent';
  if (Array.isArray(tpl)) return tpl[0]?.name?.trim() || 'Agent';
  return tpl.name?.trim() || 'Agent';
}

export function LibraryPage() {
  const hyUserId = useAppStore((s) => s.session?.hy_user_id);
  const userRole = useAppStore((s) => s.userRole);
  const { enabled: programEnabled } = useFeatureFlag('creator_program_enabled');
  const isCreator = isCreatorRole(userRole);
  const showStudioCta = isCreator && (programEnabled || userRole === 'admin' || userRole === 'superadmin');
  const [rows, setRows] = useState<InstanceRow[]>([]);
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hyUserId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const [{ data: inst, error: ie }, { data: u, error: ue }] = await Promise.all([
        supabase
          .from('user_agent_instances')
          .select('id, agent_template_id, status, updated_at, agent_templates ( name )')
          .eq('hy_user_id', hyUserId)
          .order('updated_at', { ascending: false }),
        supabase.from('users').select('credit_balance').eq('hy_user_id', hyUserId).maybeSingle(),
      ]);
      if (cancelled) return;
      if (!ie && inst) setRows(inst as unknown as InstanceRow[]);
      if (!ue && u && typeof (u as { credit_balance?: number }).credit_balance === 'number') {
        setCredits((u as { credit_balance: number }).credit_balance);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [hyUserId]);

  return (
    <div className="min-h-screen text-th-primary">
      <div className="border-b border-subtle bg-surface/80">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4">
          <div>
            <Link
              to="/"
              className="hidden text-sm text-th-secondary hover:text-th-primary md:inline"
            >
              ← Home
            </Link>
            <h1 className="mt-1 text-2xl font-bold">Your library</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {showStudioCta && (
              <Link
                to="/studio"
                className="inline-flex items-center gap-1.5 rounded-full border border-brand/40 bg-brand/10 px-3 py-1.5 text-sm text-brand hover:bg-brand/20"
                title="Open Creator Studio"
              >
                <Sparkles className="w-4 h-4" />
                Studio
              </Link>
            )}
            {credits !== null && (
              <div className="rounded-full border border-subtle bg-elevated px-4 py-1.5 text-sm">
                <span className="text-th-secondary">Credits</span>{' '}
                <span className="font-semibold tabular-nums text-th-primary">{credits}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-4 py-10">
        <p className="text-th-secondary">Agents you have subscribed to. Open one to chat.</p>
        {loading ? (
          <p className="mt-8 text-th-secondary">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="mt-8 text-th-secondary">
            No instances yet.{' '}
            <Link to="/" className="text-brand hover:underline">
              Browse the store
            </Link>
            .
          </p>
        ) : (
          <ul className="mt-8 space-y-3">
            {rows.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/apps/agent/${r.agent_template_id}?instance=${encodeURIComponent(r.id)}`}
                  className="flex items-center justify-between rounded-xl border border-subtle bg-elevated px-4 py-3 hover:border-brand/40"
                >
                  <div>
                    <p className="font-medium">{templateName(r.agent_templates)}</p>
                    <p className="text-xs text-th-tertiary">{r.status}</p>
                  </div>
                  <span className="text-sm text-brand">Open →</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
